import _ from 'lodash';

const WCAG_AA_NORMAL_RATIO = 4.5;
const WCAG_AA_LARGE_RATIO = 3.0;
const WCAG_AAA_NORMAL_RATIO = 7.0;
const WCAG_AAA_LARGE_RATIO = 4.5;


/**
 * Parse a color string (#RRGGBB, #RGB, rgb(), rgba()) into
 * normalized RGBA components (each between 0 and 1).
 */
function parseColor(color) {
	if (color.startsWith('#')) {
		// Expand shorthand #RGB to #RRGGBB
		let hex = color.slice(1);
		if (hex.length === 3) hex = hex.split('').map(x => x + x).join('');
		if (hex.length === 6) hex += 'ff'; // assume fully opaque if alpha missing
		const num = parseInt(hex, 16);
		return [
			((num / (256 ** 3)) % 256) / 255, // Red
			((num / (256 ** 2)) % 256) / 255, // Green
			((num / 256) % 256) / 255, // Blue
			(num % 256) / 255, // Alpha
		];
	}

	// Parse rgba() or rgb() formats
	const rgba = color.match(/[\d.]+/g).map(Number);
	return [
		rgba[0] / 255, // Red
		rgba[1] / 255, // Green
		rgba[2] / 255, // Blue
		rgba[3] !== undefined ? rgba[3] : 1, // Alpha
	];
}

/**
 * Blend two RGBA colors using the standard alpha compositing formula
 * @see https://en.wikipedia.org/wiki/Alpha_compositing
 */
function blend(fg, bg) {
	// Extract RGB and Alpha values for foreground (fg) and background (bg)
	const [fgR, fgG, fgB, fgA] = fg;
	const [bgR, bgG, bgB, bgA] = bg;

	// Compute the output alpha (outA)
	const outA = fgA + (bgA * (1 - fgA));

	// Compute blended R, G, B components
	const r = ((fgR * fgA) + (bgR * bgA * (1 - fgA))) / outA;
	const g = ((fgG * fgA) + (bgG * bgA * (1 - fgA))) / outA;
	const b = ((fgB * fgA) + (bgB * bgA * (1 - fgA))) / outA;

	return [r, g, b, outA];
}

/**
 * Convert sRGB values (0–1) to relative luminance (L).
 * @see https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
 */
function luminance(r, g, b) {
	// converts sRGB values into linear RGB values
	const toLin = c => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
	return (0.2126 * toLin(r)) + (0.7152 * toLin(g)) + (0.0722 * toLin(b));
}

/**
 * Compute WCAG contrast ratio between foreground and background colors.
 *
 * WCAG 2.2 Contrast Thresholds:
 *  - AA normal text: ≥ 4.5:1
 *  - AA large text: ≥ 3.0:1
 *  - AAA normal text: ≥ 7.0:1
 *  - AAA large text: ≥ 4.5:1
 *
 * Reference:
 * - https://www.w3.org/TR/WCAG22/#contrast-minimum
 * - https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
 */
function contrastRatio(fgColor, bgColor, baseColor = '#ffffff') {
	const fg = parseColor(fgColor);
	const bg = parseColor(bgColor);
	const base = parseColor(baseColor);

	// Blend background (if transparent) over the opaque base first
	const blendedBg = bg[3] < 1 ? blend(bg, base) : bg;

	// Then blend foreground (if transparent) over that result
	const blendedFg = fg[3] < 1 ? blend(fg, blendedBg) : fg;

	// Compute luminance values (per WCAG formula)
	const L1 = luminance(...blendedFg);
	const L2 = luminance(...blendedBg);

	// Contrast ratio formula (WCAG §1.4.3)
	const ratio = (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);

	// Evaluate pass/fail for different WCAG levels
	const passes = {
		AA_normal: ratio >= WCAG_AA_NORMAL_RATIO,
		AA_large: ratio >= WCAG_AA_LARGE_RATIO,
		AAA_normal: ratio >= WCAG_AAA_NORMAL_RATIO,
		AAA_large: ratio >= WCAG_AAA_LARGE_RATIO,
	};

	return { ratio: +ratio.toFixed(2), passes };
}

/**
 * Selects the color with the highest contract ratio
 */
function highestContrastColor(fgColors, bgColor, baseColor = '#ffffff') {
	return _.maxBy(fgColors, x => contrastRatio(x, bgColor, baseColor).ratio);
}

export {
	contrastRatio,
	highestContrastColor,
};



// WEBPACK FOOTER //
// ./wp-content/plugins/core/assets/theme/js/src/utils/dom/color-contrast.js